import { useState, useEffect } from "react";
import { Link } from "react-router";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import {
  fetchWorkshopSessionsWithCounts,
  fetchWorkshopTemplates,
  insertWorkshopTemplate,
  insertWorkshopSession,
  updateWorkshopTemplate,
  updateWorkshopSession,
  deleteWorkshopTemplate,
  deleteWorkshopSession,
  duplicateWorkshopSession,
  type WorkshopSessionWithCount,
} from "../supabase";
import { useAdminAuth } from "./AdminAuthContext";
import { AdminEmailNotConfiguredNotice } from "./AdminEmailNotConfiguredNotice";
import { Plus, Copy, CheckCircle, Loader2, LayoutGrid, List, Pencil, Trash2 } from "lucide-react";

const KANBAN_STATUSES: { id: string; label: string }[] = [
  { id: "scheduled", label: "Scheduled" },
  { id: "full", label: "Full" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

const columnHelper = createColumnHelper<WorkshopSessionWithCount>();

export type WorkshopTemplateRow = {
  id: string;
  slug: string;
  title: string;
  short_description?: string | null;
  description?: string | null;
  location?: string | null;
  duration_minutes?: number | null;
  max_capacity?: number | null;
  image_url?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export function AdminWorkshopSessions() {
  const { hasPermission } = useAdminAuth();
  const [activeTab, setActiveTab] = useState<"sessions" | "templates">("sessions");
  const [sessions, setSessions] = useState<WorkshopSessionWithCount[]>([]);
  const [templatesList, setTemplatesList] = useState<WorkshopTemplateRow[]>([]);
  const [templates, setTemplates] = useState<{ id: string; slug: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([{ id: "starts_at", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [templateFilter, setTemplateFilter] = useState("");
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);
  const [addSessionOpen, setAddSessionOpen] = useState(false);
  const [duplicateSessionId, setDuplicateSessionId] = useState<string | null>(null);
  const [duplicateDate, setDuplicateDate] = useState("");
  const [editTemplate, setEditTemplate] = useState<WorkshopTemplateRow | null>(null);
  const [editSession, setEditSession] = useState<WorkshopSessionWithCount | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "template" | "session"; id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");

  const load = () => {
    fetchWorkshopSessionsWithCounts().then(({ data, error: e }) => {
      if (e) setError(e.message);
      else setSessions(data ?? []);
    });
    fetchWorkshopTemplates().then(({ data }) => {
      if (data) {
        setTemplatesList(data as WorkshopTemplateRow[]);
        setTemplates(data.map((t: { id: string; slug: string; title: string }) => ({ id: t.id, slug: t.slug, title: t.title })));
      }
    });
  };

  useEffect(() => {
    if (!hasPermission("workshops")) return;
    setLoading(true);
    fetchWorkshopSessionsWithCounts().then(({ data, error: e }) => {
      setLoading(false);
      if (e) setError(e.message);
      else setSessions(data ?? []);
    });
    fetchWorkshopTemplates().then(({ data }) => {
      if (data) {
        setTemplatesList(data as WorkshopTemplateRow[]);
        setTemplates(data.map((t: { id: string; slug: string; title: string }) => ({ id: t.id, slug: t.slug, title: t.title })));
      }
    });
  }, [hasPermission]);

  if (!hasPermission("workshops")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don&apos;t have access.</p>;
  }

  const columns = [
    columnHelper.display({
      id: "mark_completed",
      header: "",
      cell: ({ row }) =>
        (row.original.status === "scheduled" || row.original.status === "full") ? (
          <button
            type="button"
            onClick={async () => { await updateWorkshopSession(row.original.id, { status: "completed" }); load(); }}
            className="flex items-center gap-1 px-2 py-1 text-[#6F675F] hover:text-[#6F7F63]"
            style={{ fontSize: "12px" }}
            title="Mark completed"
          >
            <CheckCircle size={14} />
          </button>
        ) : null,
    }),
    columnHelper.accessor((row) => row.title_override || row.template_title, { id: "title", header: "Title", cell: (info) => info.getValue() }),
    columnHelper.accessor("starts_at", {
      header: "Date",
      cell: (info) => new Date(info.getValue()).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }),
    }),
    columnHelper.accessor((row) => row.location_override || row.template_location, { id: "location", header: "Location", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.accessor("max_capacity", { header: "Capacity", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.display({
      id: "subscriber_count",
      header: "Subscribers",
      cell: ({ row }) => {
        const count = Number(row.original.subscriber_count ?? 0);
        if (count === 0) return <span>0</span>;
        return (
          <Link to={`/admin/workshops/${row.original.id}/subscribers`} className="text-[#6F7F63] hover:underline" style={{ fontSize: "12px" }}>
            {count}
          </Link>
        );
      },
    }),
    columnHelper.accessor("status", { header: "Status", cell: (info) => info.getValue() }),
    columnHelper.accessor("is_visible", { header: "Visible", cell: (info) => (info.getValue() ? "Yes" : "No") }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-2 flex-nowrap">
          <button type="button" onClick={() => setEditSession(row.original)} className="flex items-center gap-1 px-2 py-1 text-[#6F675F] hover:text-[#6F7F63]" style={{ fontSize: "12px" }} title="Edit">
            <Pencil size={14} />
          </button>
          <button type="button" onClick={() => { setDuplicateSessionId(row.original.id); setDuplicateDate(""); }} className="flex items-center gap-1 px-2 py-1 text-[#6F675F] hover:text-[#6F7F63]" style={{ fontSize: "12px" }} title="Duplicate">
            <Copy size={14} />
          </button>
          <button
            type="button"
            onClick={() => setDeleteConfirm({ type: "session", id: row.original.id, name: row.original.title_override || row.original.template_title || "Session" })}
            className="flex items-center gap-1 px-2 py-1 text-[#8B5C58] hover:text-[#A63D38]"
            style={{ fontSize: "12px" }}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: sessions,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredTemplates = templateFilter.trim()
    ? templatesList.filter(
        (t) =>
          t.title.toLowerCase().includes(templateFilter.toLowerCase()) ||
          t.slug.toLowerCase().includes(templateFilter.toLowerCase())
      )
    : templatesList;

  return (
    <div>
      <h1 className="text-[#2D2A26] tracking-[0.08em] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}>
        Workshops
      </h1>
      <AdminEmailNotConfiguredNotice />
      {error && <p className="text-[#8B5C58] mb-4" style={{ fontSize: "12px" }}>{error}</p>}

      <div className="flex items-center gap-1 border-b border-[#E6E0D7] mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("sessions")}
          className={`px-4 py-3 border-b-2 -mb-px transition-colors ${activeTab === "sessions" ? "border-[#6F7F63] text-[#2D2A26] font-medium" : "border-transparent text-[#6F675F] hover:text-[#2D2A26]"}`}
          style={{ fontSize: "13px" }}
        >
          Sessions
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("templates")}
          className={`px-4 py-3 border-b-2 -mb-px transition-colors ${activeTab === "templates" ? "border-[#6F7F63] text-[#2D2A26] font-medium" : "border-transparent text-[#6F675F] hover:text-[#2D2A26]"}`}
          style={{ fontSize: "13px" }}
        >
          Templates
        </button>
      </div>

      {activeTab === "sessions" && (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              type="button"
              onClick={() => setAddTemplateOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F] hover:bg-[#F2EEE8]"
              style={{ fontSize: "12px" }}
            >
              <Plus size={14} /> Add template
            </button>
            <button
              type="button"
              onClick={() => setAddSessionOpen(true)}
              disabled={templates.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] disabled:opacity-50"
              style={{ fontSize: "12px" }}
            >
              <Plus size={14} /> Add session
            </button>
            <div className="flex items-center gap-1 border border-[#E6E0D7] rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-2.5 ${viewMode === "table" ? "bg-[#6F7F63] text-white" : "text-[#6F675F] hover:bg-[#F2EEE8]"}`}
                style={{ fontSize: "12px" }}
                title="List view"
              >
                <List size={14} />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-1.5 px-3 py-2.5 ${viewMode === "kanban" ? "bg-[#6F7F63] text-white" : "text-[#6F675F] hover:bg-[#F2EEE8]"}`}
                style={{ fontSize: "12px" }}
                title="Kanban view"
              >
                <LayoutGrid size={14} />
                Kanban
              </button>
            </div>
            <input
              type="text"
              placeholder="Search sessions…"
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="px-4 py-2.5 border border-[#E6E0D7] rounded-lg max-w-xs"
              style={{ fontSize: "13px" }}
            />
          </div>

      {loading ? (
        <p className="text-[#6F675F]">Loading…</p>
      ) : viewMode === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_STATUSES.map((col) => {
            const columnSessions = sessions.filter(
              (s) => s.status === col.id && (!globalFilter || [s.title_override, s.template_title, s.location_override, s.template_location].some((v) => String(v ?? "").toLowerCase().includes(globalFilter.toLowerCase())))
            );
            return (
              <div
                key={col.id}
                className="shrink-0 w-72 rounded-xl border border-[#E6E0D7] bg-[#F7F5F1]/50"
              >
                <div className="px-4 py-3 border-b border-[#E6E0D7] flex items-center justify-between">
                  <span className="font-medium text-[#2D2A26]" style={{ fontSize: "13px" }}>{col.label}</span>
                  <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>{columnSessions.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {columnSessions.map((session) => {
                    const title = session.title_override || session.template_title;
                    const location = session.location_override || session.template_location;
                    const maxCap = session.max_capacity ?? 0;
                    const count = Number(session.subscriber_count ?? 0);
                    return (
                      <div
                        key={session.id}
                        className="bg-white rounded-lg border border-[#E6E0D7] p-3 shadow-sm"
                      >
                        <p className="font-medium text-[#2D2A26] mb-1" style={{ fontSize: "12px" }}>{title}</p>
                        <p className="text-[#6F675F] mb-2" style={{ fontSize: "11px" }}>
                          {new Date(session.starts_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                        </p>
                        {location && <p className="text-[#6F675F] mb-2 truncate" style={{ fontSize: "11px" }}>{location}</p>}
                        <p className="text-[#6F675F] mb-3" style={{ fontSize: "11px" }}>
                          {count > 0 ? (
                            <Link to={`/admin/workshops/${session.id}/subscribers`} className="text-[#6F7F63] hover:underline">{count}</Link>
                          ) : (
                            count
                          )}
                          {maxCap > 0 ? ` / ${maxCap}` : ""} spots
                        </p>
                        <div className="flex items-center gap-1 flex-nowrap">
                          {(session.status === "scheduled" || session.status === "full") && (
                            <button type="button" onClick={async () => { await updateWorkshopSession(session.id, { status: "completed" }); load(); }} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#6F675F] hover:bg-[#E6E0D7]" style={{ fontSize: "11px" }} title="Mark completed"><CheckCircle size={12} /></button>
                          )}
                          <button type="button" onClick={() => setEditSession(session)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#6F675F] hover:bg-[#E6E0D7]" style={{ fontSize: "11px" }} title="Edit"><Pencil size={12} /></button>
                          <button type="button" onClick={() => { setDuplicateSessionId(session.id); setDuplicateDate(""); }} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#6F675F] hover:bg-[#E6E0D7]" style={{ fontSize: "11px" }} title="Duplicate"><Copy size={12} /></button>
                          <button type="button" onClick={() => setDeleteConfirm({ type: "session", id: session.id, name: title ?? "Session" })} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#8B5C58] hover:bg-[#F5E8E8]" style={{ fontSize: "11px" }} title="Delete"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-auto max-h-[70vh]">
          <table className="w-full min-w-[900px]">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-[#E6E0D7]">
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase"
                      style={{ fontSize: "10px", fontWeight: 400 }}
                    >
                      <span className={h.column.getCanSort() ? "cursor-pointer" : ""} onClick={() => h.column.toggleSorting()}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() && (h.column.getIsSorted() === "asc" ? " ↑" : " ↓")}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-5 py-3" style={{ fontSize: "13px" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sessions.length === 0 && !loading && (
        <p className="text-[#6F675F] mt-6">No sessions yet. Add a template (Templates tab), then add a session.</p>
      )}
        </>
      )}

      {activeTab === "templates" && (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              type="button"
              onClick={() => setAddTemplateOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53]"
              style={{ fontSize: "12px" }}
            >
              <Plus size={14} /> Add template
            </button>
            <input
              type="text"
              placeholder="Search templates…"
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              className="px-4 py-2.5 border border-[#E6E0D7] rounded-lg max-w-xs"
              style={{ fontSize: "13px" }}
            />
          </div>
          {loading ? (
            <p className="text-[#6F675F]">Loading…</p>
          ) : (
            <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E6E0D7]">
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Title</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Slug</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Short description</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Duration</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Capacity</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Image</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Active</th>
                    <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map((t) => (
                    <tr key={t.id} className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50">
                      <td className="px-5 py-3 font-medium text-[#2D2A26]" style={{ fontSize: "13px" }}>{t.title}</td>
                      <td className="px-5 py-3 text-[#6F675F]" style={{ fontSize: "12px" }}>{t.slug}</td>
                      <td className="px-5 py-3 text-[#6F675F] max-w-[200px] truncate" style={{ fontSize: "12px" }} title={t.short_description ?? undefined}>{t.short_description ?? "—"}</td>
                      <td className="px-5 py-3 text-[#6F675F]" style={{ fontSize: "12px" }}>{t.duration_minutes != null ? `${t.duration_minutes} min` : "—"}</td>
                      <td className="px-5 py-3 text-[#6F675F]" style={{ fontSize: "12px" }}>{t.max_capacity ?? "—"}</td>
                      <td className="px-5 py-3" style={{ fontSize: "12px" }}>{t.image_url ? "Yes" : "—"}</td>
                      <td className="px-5 py-3 text-[#6F675F]" style={{ fontSize: "12px" }}>{t.is_active ? "Yes" : "No"}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setEditTemplate(t)} className="p-1.5 text-[#6F675F] hover:text-[#6F7F63] hover:bg-[#E8EDE5] rounded" title="Edit"><Pencil size={14} /></button>
                          <button type="button" onClick={() => setDeleteConfirm({ type: "template", id: t.id, name: t.title })} className="p-1.5 text-[#8B5C58] hover:text-[#A63D38] hover:bg-[#F5E8E8] rounded" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredTemplates.length === 0 && !loading && (
            <p className="text-[#6F675F] mt-6">No templates yet. Add a template to define workshop types; then add sessions from the Sessions tab.</p>
          )}
        </>
      )}

      {/* Add template modal */}
      {addTemplateOpen && (
        <AddTemplateModal
          onClose={() => setAddTemplateOpen(false)}
          onSaved={() => {
            setAddTemplateOpen(false);
            load();
            fetchWorkshopTemplates().then(({ data }) => {
              if (data) {
                setTemplatesList(data as WorkshopTemplateRow[]);
                setTemplates(data.map((t: { id: string; slug: string; title: string }) => ({ id: t.id, slug: t.slug, title: t.title })));
              }
            });
          }}
        />
      )}

      {/* Add session modal */}
      {addSessionOpen && (
        <AddSessionModal
          templates={templates}
          onClose={() => setAddSessionOpen(false)}
          onSaved={() => { setAddSessionOpen(false); load(); }}
        />
      )}

      {/* Duplicate modal */}
      {duplicateSessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-[#2D2A26] mb-4" style={{ fontSize: "16px" }}>Duplicate session to new date</h3>
            <input
              type="datetime-local"
              value={duplicateDate}
              onChange={(e) => setDuplicateDate(e.target.value)}
              className="w-full px-4 py-2.5 border border-[#E6E0D7] rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button onClick={() => setDuplicateSessionId(null)} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
              <button
                onClick={async () => {
                  if (!duplicateDate) return;
                  const { error: e } = await duplicateWorkshopSession(duplicateSessionId, new Date(duplicateDate).toISOString());
                  if (e) setError(e.message);
                  else { setDuplicateSessionId(null); load(); }
                }}
                disabled={!duplicateDate}
                className="flex-1 py-2.5 bg-[#6F7F63] text-white rounded-lg disabled:opacity-50"
              >
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit template modal */}
      {editTemplate && (
        <EditTemplateModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSaved={() => {
            setEditTemplate(null);
            load();
            fetchWorkshopTemplates().then(({ data }) => {
              if (data) {
                setTemplatesList(data as WorkshopTemplateRow[]);
                setTemplates(data.map((t: { id: string; slug: string; title: string }) => ({ id: t.id, slug: t.slug, title: t.title })));
              }
            });
          }}
        />
      )}

      {/* Edit session modal */}
      {editSession && (
        <EditSessionModal
          session={editSession}
          templates={templates}
          onClose={() => setEditSession(null)}
          onSaved={() => { setEditSession(null); load(); }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-[#2D2A26] mb-2" style={{ fontSize: "16px" }}>Delete {deleteConfirm.type === "template" ? "template" : "session"}?</h3>
            <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px" }}>
              &ldquo;{deleteConfirm.name}&rdquo; will be permanently removed. This cannot be undone.
              {deleteConfirm.type === "template" && " All sessions and subscriptions for this template will also be deleted."}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
              <button
                onClick={async () => {
                  const err = deleteConfirm.type === "template"
                    ? (await deleteWorkshopTemplate(deleteConfirm.id)).error
                    : (await deleteWorkshopSession(deleteConfirm.id)).error;
                  if (err) setError(err.message);
                  else { setDeleteConfirm(null); load(); }
                }}
                className="flex-1 py-2.5 bg-[#8B5C58] text-white rounded-lg hover:bg-[#7A4C48]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddTemplateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [maxCapacity, setMaxCapacity] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!slug.trim() || !title.trim()) return;
    setSaving(true);
    const { error } = await insertWorkshopTemplate({
      slug: slug.trim(),
      title: title.trim(),
      short_description: shortDescription.trim() || null,
      description: description.trim() || null,
      location: location.trim() || null,
      duration_minutes: durationMinutes.trim() ? parseInt(durationMinutes, 10) : null,
      max_capacity: maxCapacity.trim() ? parseInt(maxCapacity, 10) : null,
      image_url: imageUrl.trim() || null,
      is_active: isActive,
    });
    setSaving(false);
    if (error) return;
    onSaved();
  };

  const labelClass = "block text-[#6F675F] text-xs uppercase mb-1";
  const inputClass = "w-full px-4 py-2.5 border border-[#E6E0D7] rounded-lg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] flex flex-col">
        <h3 className="text-[#2D2A26] mb-4" style={{ fontSize: "16px" }}>New workshop template</h3>
        <p className="text-[#6F675F] text-xs mb-4">A template defines the full workshop (title, description, location, capacity). Sessions are scheduled instances of a template.</p>
        <p className="text-[#6F7F63] text-xs mb-4 p-3 rounded-lg bg-[#E8EDE5]/50">If you set a <strong>max capacity</strong>, the waiting list will be used when sessions are full. If left empty, sessions can be planned flexibly without a fixed limit.</p>
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
          <div>
            <label className={labelClass}>Slug (unique)</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} className={inputClass} placeholder="matcha-ceremony" />
          </div>
          <div>
            <label className={labelClass}>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="Matcha Ceremony" />
          </div>
          <div>
            <label className={labelClass}>Short description</label>
            <input value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} className={inputClass} placeholder="One line for cards" />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Full workshop description" />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} className={inputClass} placeholder="Kyoto Studio, Tokyo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input type="number" min={1} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} className={inputClass} placeholder="120" />
            </div>
            <div>
              <label className={labelClass}>Max capacity</label>
              <input type="number" min={0} value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)} className={inputClass} placeholder="12" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Image URL (card/hero)</label>
            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inputClass} placeholder="https://…" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-[#E6E0D7]" />
            <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>Active (visible for new sessions)</span>
          </label>
        </div>
        <div className="flex gap-3 mt-6 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
          <button onClick={handle} disabled={saving || !slug.trim() || !title.trim()} className="flex-1 py-2.5 bg-[#6F7F63] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Create template
          </button>
        </div>
      </div>
    </div>
  );
}

function AddSessionModal({
  templates,
  onClose,
  onSaved,
}: {
  templates: { id: string; slug: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [templateId, setTemplateId] = useState("");
  const [titleOverride, setTitleOverride] = useState("");
  const [locationOverride, setLocationOverride] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [maxCapacityOverride, setMaxCapacityOverride] = useState("");
  const [status, setStatus] = useState("scheduled");
  const [isVisible, setIsVisible] = useState(true);
  const [notesInternal, setNotesInternal] = useState("");
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!templateId || !startsAt) return;
    setSaving(true);
    const { error } = await insertWorkshopSession({
      template_id: templateId,
      title_override: titleOverride.trim() || null,
      location_override: locationOverride.trim() || null,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      max_capacity_override: maxCapacityOverride.trim() ? parseInt(maxCapacityOverride, 10) : null,
      status,
      is_visible: isVisible,
      notes_internal: notesInternal.trim() || null,
    });
    setSaving(false);
    if (error) return;
    onSaved();
  };

  const labelClass = "block text-[#6F675F] text-xs uppercase mb-1";
  const inputClass = "w-full px-4 py-2.5 border border-[#E6E0D7] rounded-lg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] flex flex-col">
        <h3 className="text-[#2D2A26] mb-4" style={{ fontSize: "16px" }}>New session</h3>
        <p className="text-[#6F675F] text-xs mb-4">A session is a scheduled instance of a workshop template. Overrides are optional (otherwise template values are used).</p>
        <p className="text-[#6F7F63] text-xs mb-4 p-3 rounded-lg bg-[#E8EDE5]/50">If you set a <strong>max capacity</strong>, the waiting list will be used when the session is full (people can join the list and be notified when a spot opens). If you leave capacity empty, sessions can be planned flexibly without a fixed limit.</p>
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
          <div>
            <label className={labelClass}>Template</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputClass}>
              <option value="">Select template</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Starts at</label>
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Ends at (optional)</label>
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Title override (optional)</label>
            <input value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} className={inputClass} placeholder="Uses template title if empty" />
          </div>
          <div>
            <label className={labelClass}>Location override (optional)</label>
            <input value={locationOverride} onChange={(e) => setLocationOverride(e.target.value)} className={inputClass} placeholder="Uses template location if empty" />
          </div>
          <div>
            <label className={labelClass}>Max capacity override (optional)</label>
            <input type="number" min={0} value={maxCapacityOverride} onChange={(e) => setMaxCapacityOverride(e.target.value)} className={inputClass} placeholder="Uses template capacity if empty" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                <option value="scheduled">Scheduled</option>
                <option value="full">Full</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end pb-2.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} className="rounded border-[#E6E0D7]" />
                <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>Visible on site</span>
              </label>
            </div>
          </div>
          <div>
            <label className={labelClass}>Internal notes (optional)</label>
            <textarea value={notesInternal} onChange={(e) => setNotesInternal(e.target.value)} className={inputClass} rows={2} placeholder="Admin only" />
          </div>
        </div>
        <div className="flex gap-3 mt-6 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
          <button onClick={handle} disabled={saving || !templateId || !startsAt} className="flex-1 py-2.5 bg-[#6F7F63] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Create session
          </button>
        </div>
      </div>
    </div>
  );
}

function EditTemplateModal({
  template,
  onClose,
  onSaved,
}: {
  template: WorkshopTemplateRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [slug, setSlug] = useState(template.slug);
  const [title, setTitle] = useState(template.title);
  const [shortDescription, setShortDescription] = useState(template.short_description ?? "");
  const [description, setDescription] = useState(template.description ?? "");
  const [location, setLocation] = useState(template.location ?? "");
  const [durationMinutes, setDurationMinutes] = useState(template.duration_minutes != null ? String(template.duration_minutes) : "");
  const [maxCapacity, setMaxCapacity] = useState(template.max_capacity != null ? String(template.max_capacity) : "");
  const [imageUrl, setImageUrl] = useState(template.image_url ?? "");
  const [isActive, setIsActive] = useState(template.is_active);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!slug.trim() || !title.trim()) return;
    setSaving(true);
    const { error } = await updateWorkshopTemplate(template.id, {
      slug: slug.trim(),
      title: title.trim(),
      short_description: shortDescription.trim() || null,
      description: description.trim() || null,
      location: location.trim() || null,
      duration_minutes: durationMinutes.trim() ? parseInt(durationMinutes, 10) : null,
      max_capacity: maxCapacity.trim() ? parseInt(maxCapacity, 10) : null,
      image_url: imageUrl.trim() || null,
      is_active: isActive,
    });
    setSaving(false);
    if (error) return;
    onSaved();
  };

  const labelClass = "block text-[#6F675F] text-xs uppercase mb-1";
  const inputClass = "w-full px-4 py-2.5 border border-[#E6E0D7] rounded-lg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] flex flex-col">
        <h3 className="text-[#2D2A26] mb-4" style={{ fontSize: "16px" }}>Edit template</h3>
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
          <div>
            <label className={labelClass}>Slug (unique)</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} className={inputClass} placeholder="matcha-ceremony" />
          </div>
          <div>
            <label className={labelClass}>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="Matcha Ceremony" />
          </div>
          <div>
            <label className={labelClass}>Short description</label>
            <input value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} className={inputClass} placeholder="One line for cards" />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Full workshop description" />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} className={inputClass} placeholder="Kyoto Studio, Tokyo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input type="number" min={1} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} className={inputClass} placeholder="120" />
            </div>
            <div>
              <label className={labelClass}>Max capacity</label>
              <input type="number" min={0} value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)} className={inputClass} placeholder="12" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Image URL (card/hero)</label>
            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inputClass} placeholder="https://…" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-[#E6E0D7]" />
            <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>Active (visible for new sessions)</span>
          </label>
        </div>
        <div className="flex gap-3 mt-6 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
          <button onClick={handle} disabled={saving || !slug.trim() || !title.trim()} className="flex-1 py-2.5 bg-[#6F7F63] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function EditSessionModal({
  session,
  templates,
  onClose,
  onSaved,
}: {
  session: WorkshopSessionWithCount;
  templates: { id: string; slug: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const templateTitle = session.template_title ?? templates.find((t) => t.id === session.template_id)?.title ?? session.template_id;
  const [titleOverride, setTitleOverride] = useState(session.title_override ?? "");
  const [locationOverride, setLocationOverride] = useState(session.location_override ?? session.template_location ?? "");
  const [startsAt, setStartsAt] = useState(session.starts_at ? new Date(session.starts_at).toISOString().slice(0, 16) : "");
  const [endsAt, setEndsAt] = useState(session.ends_at ? new Date(session.ends_at).toISOString().slice(0, 16) : "");
  const [maxCapacityOverride, setMaxCapacityOverride] = useState(session.max_capacity_override != null ? String(session.max_capacity_override) : "");
  const [status, setStatus] = useState(session.status);
  const [isVisible, setIsVisible] = useState(session.is_visible);
  const [notesInternal, setNotesInternal] = useState(session.notes_internal ?? "");
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!startsAt) return;
    setSaving(true);
    const { error } = await updateWorkshopSession(session.id, {
      title_override: titleOverride.trim() || null,
      location_override: locationOverride.trim() || null,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      max_capacity_override: maxCapacityOverride.trim() ? parseInt(maxCapacityOverride, 10) : null,
      status,
      is_visible: isVisible,
      notes_internal: notesInternal.trim() || null,
    });
    setSaving(false);
    if (error) return;
    onSaved();
  };

  const labelClass = "block text-[#6F675F] text-xs uppercase mb-1";
  const inputClass = "w-full px-4 py-2.5 border border-[#E6E0D7] rounded-lg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] flex flex-col">
        <h3 className="text-[#2D2A26] mb-4" style={{ fontSize: "16px" }}>Edit session</h3>
        <p className="text-[#6F7F63] text-xs mb-4 p-3 rounded-lg bg-[#E8EDE5]/50">A set <strong>max capacity</strong> enables the waiting list when full; leave empty for flexible planning.</p>
        <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
          <div>
            <label className={labelClass}>Template</label>
            <input value={templateTitle} readOnly className={inputClass + " bg-[#F7F5F1] cursor-not-allowed"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Starts at</label>
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Ends at (optional)</label>
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Title override (optional)</label>
            <input value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} className={inputClass} placeholder="Uses template title if empty" />
          </div>
          <div>
            <label className={labelClass}>Location override (optional)</label>
            <input value={locationOverride} onChange={(e) => setLocationOverride(e.target.value)} className={inputClass} placeholder="Uses template location if empty" />
          </div>
          <div>
            <label className={labelClass}>Max capacity override (optional)</label>
            <input type="number" min={0} value={maxCapacityOverride} onChange={(e) => setMaxCapacityOverride(e.target.value)} className={inputClass} placeholder="Uses template capacity if empty" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                <option value="scheduled">Scheduled</option>
                <option value="full">Full</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end pb-2.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} className="rounded border-[#E6E0D7]" />
                <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>Visible on site</span>
              </label>
            </div>
          </div>
          <div>
            <label className={labelClass}>Internal notes (optional)</label>
            <textarea value={notesInternal} onChange={(e) => setNotesInternal(e.target.value)} className={inputClass} rows={2} placeholder="Admin only" />
          </div>
        </div>
        <div className="flex gap-3 mt-6 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F]">Cancel</button>
          <button onClick={handle} disabled={saving || !startsAt} className="flex-1 py-2.5 bg-[#6F7F63] text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
